<template>
    <div class="containerI mt-2">
        <div class="language-selector">
            <div class="languageContainer">
                <div v-for="lang in languages" :key="lang.languageId" class="language-wrapper"
                    @mouseenter="hoveredLang = lang.languageId" @mouseleave="hoveredLang = null">

                    <button :class="['language-btn', { active: selectedLanguage === lang.languageId }]"
                        @click="handleButtonClick(lang)" :ref="'langBtn-' + lang.languageId" :disabled="isEditOpen"
                        :style="{ cursor: isEditOpen ? 'not-allowed' : 'pointer' }">
                        {{ lang.languageName }}
                    </button>
                </div>
            </div>
            <div class="addDeleteBtn">
                <button class="action-btn add" @click="openAddLanguagePopup"
                    v-if="languages.length < availableLanguages.length" :disabled="isEditOpen"
                    :style="{ cursor: isEditOpen ? 'not-allowed' : 'pointer' }">
                    <i class="bi bi-plus"></i> Add
                </button>
                <button class="action-btn add" @click="openDeleteLanguagePopup" v-if="languages.length > 0"
                    :disabled="isEditOpen" :style="{ cursor: isEditOpen ? 'not-allowed' : 'pointer' }">
                    <i class="bi bi-trash"></i> Delete
                </button>

            </div>
        </div>
        <!-- Test Instructions Section -->
        <div class="section" :class="{ 'expanded': testEditing }">
            <div class="section-header">
                <h2 class="section-title">Test Instructions</h2>
                <div class="section-actions">
                    <button v-if="!testEditing" class="action-btn" @click="toggleTestView">
                        <i :class="!testExpanded ? 'bi bi-arrows-fullscreen' : 'bi bi-arrows-angle-contract'"></i>
                        {{ !testExpanded ? 'View Full' : 'View Less' }}
                    </button>
                    <button v-if="!testEditing" class="action-btn" @click="startTestEdit" :disabled="isEditOpen"
                        :style="{ cursor: isEditOpen ? 'not-allowed' : 'pointer' }">
                        <i class="bi bi-pencil-square"></i>
                        Edit
                    </button>
                </div>
            </div>
            <div v-if="!testEditing">
                <div class="instruction-content" :class="{ 'collapsed-text': !testExpanded }"
                    v-html="sanitizedTestInstruction">
                </div>
            </div>
            <div v-else>
                <QuillEditor id="test-editor" v-model:content="testEditContent" contentType="html"
                    :options="editorOptions" class="instruction-content" />
                <div class="section-actions mt-2 float-end">
                    <button class="action-btn cancel" @click="cancelTestEdit">
                        Cancel
                    </button>
                    <button class="action-btn primary" @click="saveTestInstructions">
                        Save
                    </button>
                </div>
            </div>
        </div>
        <!-- Section Instructions -->
        <div class="section" id="section-container" :class="{ 'expanded': sectionEditing }">
            <div class="section-header">
                <h2 class="section-title">Section Instructions</h2>
                <div class="section-actions">
                    <div class="custom-dropdown">
                        <select v-model="selectedSection" @change="selectSection($event.target.value)"
                            @focus="isOpen = true" @blur="handleBlur" :disabled="isEditOpen"
                            :style="{ cursor: isEditOpen ? 'not-allowed' : 'pointer' }">
                            <option disabled value="">Select Section</option>
                            <option v-for="section in sections" :key="section.secId" :value="section.secId">
                                {{ section.sectionName }}
                            </option>
                        </select>
                        <i class="dropdown-icon bi" :class="isOpen ? 'bi-caret-up-fill' : 'bi-caret-down-fill'"></i>
                    </div>
                    <button v-if="!sectionEditing" class="action-btn" @click="toggleSectionView">
                        <i :class="!sectionExpanded ? 'bi bi-arrows-fullscreen' : 'bi bi-arrows-angle-contract'"></i>
                        {{ !sectionExpanded ? 'View Full' : 'View Less' }}
                    </button>
                    <button v-if="!sectionEditing" class="action-btn" @click="startSectionEdit" :disabled="isEditOpen"
                        :style="{ cursor: isEditOpen ? 'not-allowed' : 'pointer' }">
                        <i class="bi bi-pencil-square"></i>
                        Edit
                    </button>
                </div>
            </div>
            <div v-if="!sectionEditing">
                <div class="instruction-content" :class="{ 'collapsed-text': !sectionExpanded }"
                    v-html="sanitizedSectionInstruction">
                </div>
            </div>
            <div v-else>
                <QuillEditor id="section-editor" v-model:content="sectionEditContent" contentType="html"
                    :options="editorOptions" class="instruction-content" />

                <div class="section-actions mt-2 float-end">
                    <template v-if="sectionEditing">
                        <button class="action-btn cancel" @click="cancelSectionEdit">
                            Cancel
                        </button>
                        <button class="action-btn primary" @click="saveSectionInstructions">
                            Save
                        </button>
                    </template>
                </div>
            </div>
        </div>

        <div v-if="showAddLanguagePopup || showDeleteLanguagePopup" class="popup-overlay">
            <div class="popup-content">
                <h4 class="mb-3">{{ languagePopupText }}</h4>
                <!-- <p>Select a language to add:</p> -->
                <select v-model="newLanguage" class="language-select">
                    <option :value="null" disabled>Select Language to add</option>
                    <option v-for="lang in languageListInPopup" :key="lang.languageId" :value="lang.languageId">
                        {{ lang.languageName }}
                    </option>
                </select>

                <div class="popup-actions">
                    <button class="action-btn cancel" @click="closeLanguagePopup">Cancel</button>
                    <button class="action-btn primary"
                        @click="() => showAddLanguagePopup ? addLanguage() : deleteLanguage()"
                        :class="{ 'is-disabled': isLanguageSelected }" type="button">
                        {{ showAddLanguagePopup ? 'Add Language' : 'Delete Language' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";
import { QuillEditor } from '@vueup/vue-quill';
import 'quill/dist/quill.snow.css';
import { toast } from "vue3-toastify";

export default {
    name: "ActiveTestInstructions",
    components: {
        QuillEditor
    },
    data() {
        return {
            editTestName: '',
            isOpen: false,
            routetestId: window.atob(this.$route.query.id),
            languages: [],
            selectedLanguage: null,
            instruction: '',
            testExpanded: false,
            testEditing: false,
            testEditContent: '',
            sectionExpanded: false,
            sectionEditing: false,
            sectionEditContent: '',
            sectionInstruction: '',
            selectedSection: null,
            sections: [],
            showAddLanguagePopup: false,
            showDeleteLanguagePopup: false,
            languagePopupText: '',
            newLanguage: null,
            availableLanguages: [],
            deleteMode: false,
            allSectionInstructions: [],
            hoveredLang: null,
            langToDelete: null,
            editorOptions: {
                theme: 'snow',
                modules: {
                    toolbar: [
                        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        [{ 'indent': '-1' }, { 'indent': '+1' }],
                        [{ 'align': [] }],
                        ['link', 'blockquote', 'code-block', 'image', 'video'],
                        ['clean']
                    ]
                }
            }
        };
    },
    computed: {
        sanitizedTestInstruction() {
            return this.sanitizeHtmlForQuill(this.instruction);
        },
        sanitizedSectionInstruction() {
            return this.sanitizeHtmlForQuill(this.sectionInstruction);
        },
        isLanguageSelected() {
            return this.newLanguage == null;
        },
        languageForAddPopup() {
            const testLanguageIds = new Set(this.languages.map(lang => lang.languageId));
            return this.availableLanguages.filter(lang => !testLanguageIds.has(lang.languageId));
        },
        languageListInPopup() {
            return this.showAddLanguagePopup ? this.languageForAddPopup : this.languages;
        },
        isEditOpen() {
            return this.sectionEditing || this.testEditing;
        }
    },
    mounted() {
        this.TestDetails();
        this.GetTestSections();
        this.GetTestLanguages();
        this.fetchAvailableLanguages();
    },
    methods: {
        ...mapActions([
            "activetestdetail",
            "GetTestSettingTestsLanguages",
            "getTestInstruction",
            "getTestSectionInstructions",
            "getAllLanguages",
            "getTestSectionsList",
            "putTestInstruction",
            "putTestSectionInstructions",
            "createTestLanguage",
            "deleteTestLanguage"
        ]),
        handleButtonClick(lang) {
            if (this.selectedLanguage === lang.languageId && this.hoveredLang === lang.languageId && this.canShowDelete) {
                this.prepareDelete(lang);
            } else {
                this.selectLanguage(lang.languageId);
                this.canShowDelete = false;
                clearTimeout(this.deleteTimeout);
                this.deleteTimeout = setTimeout(() => {
                    this.canShowDelete = true;
                }, 100);
            }
        },

        sanitizeHtmlForQuill(html) {
            if (!html) return '';

            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;

            this.convertUnsupportedTags(tempDiv);

            this.cleanAttributes(tempDiv);

            return tempDiv.innerHTML;
        },

        convertUnsupportedTags(element) {
            const divs = element.querySelectorAll('div');
            divs.forEach(div => {
                const p = document.createElement('p');
                p.innerHTML = div.innerHTML;
                if (div.style.textAlign) p.style.textAlign = div.style.textAlign;
                if (div.className && this.isSupportedClass(div.className)) p.className = div.className;
                div.parentNode.replaceChild(p, div);
            });

            const spans = element.querySelectorAll('span');
            spans.forEach(span => {
                if (span.style.fontWeight === 'bold' || span.style.fontWeight >= 600) {
                    const strong = document.createElement('strong');
                    strong.innerHTML = span.innerHTML;
                    span.parentNode.replaceChild(strong, span);
                } else if (span.style.fontStyle === 'italic') {
                    const em = document.createElement('em');
                    em.innerHTML = span.innerHTML;
                    span.parentNode.replaceChild(em, span);
                } else if (span.style.textDecoration === 'underline') {
                    const u = document.createElement('u');
                    u.innerHTML = span.innerHTML;
                    span.parentNode.replaceChild(u, span);
                } else if (span.style.color || span.style.backgroundColor) {
                    span.removeAttribute('style');
                    // if (span.style.color) span.style.color = span.style.color;
                    // if (span.style.backgroundColor) span.style.backgroundColor = span.style.backgroundColor;
                } else if (!span.innerHTML.trim()) {
                   
                    span.remove();
                } else {
                    
                    span.outerHTML = span.innerHTML;
                }
            });

            for (let i = 7; i <= 9; i++) {
                const headings = element.querySelectorAll(`h${i}`);
                headings.forEach(heading => {
                    const h6 = document.createElement('h6');
                    h6.innerHTML = heading.innerHTML;
                    heading.parentNode.replaceChild(h6, heading);
                });
            }

            const scripts = element.querySelectorAll('script, style');
            scripts.forEach(script => script.remove());

            const tables = element.querySelectorAll('table');
            tables.forEach(table => {
                const tableText = table.innerText;
                const p = document.createElement('p');
                p.innerHTML = `<strong>Table Content:</strong><br>${tableText.replace(/\n/g, '<br>')}`;
                table.parentNode.replaceChild(p, table);
            });
        },

        cleanAttributes(element) {
            const allElements = element.querySelectorAll('*');
            allElements.forEach(el => {

                const allowedAttributes = {
                    'a': ['href', 'target'],
                    'img': ['src', 'alt', 'width', 'height'],
                    'p': ['style'],
                    'span': ['style'],
                    'h1': ['style'], 'h2': ['style'], 'h3': ['style'],
                    'h4': ['style'], 'h5': ['style'], 'h6': ['style'],
                    'ol': ['style'], 'ul': ['style'], 'li': ['style'],
                    'blockquote': ['style'],
                    'strong': [], 'em': [], 'u': [], 's': []
                };

                const tagName = el.tagName.toLowerCase();
                const allowed = allowedAttributes[tagName] || [];

                Array.from(el.attributes).forEach(attr => {
                    if (!allowed.includes(attr.name)) {
                        el.removeAttribute(attr.name);
                    }
                });

                if (el.hasAttribute('style')) {
                    this.cleanStyleAttribute(el);
                }
            });
        },

        cleanStyleAttribute(element) {
            const style = element.style;
            const supportedStyles = {};

            if (style.color) supportedStyles.color = style.color;
            if (style.backgroundColor) supportedStyles.backgroundColor = style.backgroundColor;
            if (style.textAlign) supportedStyles.textAlign = style.textAlign;
            if (style.fontSize) supportedStyles.fontSize = style.fontSize;
            if (style.fontFamily) supportedStyles.fontFamily = style.fontFamily;

            element.removeAttribute('style');
            Object.keys(supportedStyles).forEach(prop => {
                element.style[prop] = supportedStyles[prop];
            });
        },

        isSupportedClass(className) {
            const supportedClasses = ['ql-align-center', 'ql-align-right', 'ql-align-justify'];
            return supportedClasses.some(cls => className.includes(cls));
        },

        async TestDetails() {
            try {
                const testdetail = await this.activetestdetail({
                    testId: this.routetestId
                });
                this.editTestName = testdetail.object.data.testName;
            } catch (error) {
                console.error('Error fetching test details:', error);
            }
        },

        async GetTestLanguages() {
            try {
                const language = await this.GetTestSettingTestsLanguages({
                    TestId: this.routetestId
                });
                this.languages = language.object.data || [];
                if (this.languages.length > 0) {
                    this.selectedLanguage = this.languages[0].languageId;
                    await this.loadInstructions();
                }
            } catch (error) {
                toast.error('Failed to load test languages.');
            }
        },

        async GetTestSections() {
            try {
                const sections = await this.getTestSectionsList({
                    testId: this.routetestId
                });
                if (sections.status) {
                    this.sections = sections.object.data.map(section => ({
                        secId: section.secId,
                        sectionName: section.sectionName
                    }));
                    if (this.sections.length > 0) {
                        this.selectedSection = this.sections[0].secId;
                        await this.loadInstructions();
                    }
                }
            } catch (error) {
                toast.error('Failed to load test sections.');
            }
        },

        async loadInstructions() {
            if (!this.selectedLanguage || !this.selectedSection) return;
            try {
                const data = {
                    testId: this.routetestId,
                    languageId: parseInt(this.selectedLanguage)
                };

                const testResponse = await this.getTestInstruction(data);
                this.instruction = testResponse.status && testResponse.object.data
                    ? testResponse.object.data.instruction
                    : '';

                const sectionResponse = await this.getTestSectionInstructions(data);

                if (sectionResponse.status && sectionResponse.object.data) {
                    this.allSectionInstructions = sectionResponse.object.data;
                } else {
                    this.allSectionInstructions = [];
                }
                this.filterSectionInstruction();
            } catch (error) {
                toast.error('Failed to load instructions.', error);
            }
        },

        async filterSectionInstruction() {
            this.sectionInstruction = this.allSectionInstructions.find(s =>
                s.secId === this.selectedSection && s.languageId === this.selectedLanguage
            )?.instruction || '';
        },

        async fetchAvailableLanguages() {
            try {
                const response = await this.getAllLanguages();
                if (response.status) {
                    this.availableLanguages = response.object.data || [];
                } else {
                    this.availableLanguages = [];
                }
            } catch (error) {
                toast.error('Failed to load available languages.');
            }
        },

        async addLanguage() {
            if (!this.newLanguage) {
                toast.success("Please select a language to add.");
                return;
            }
            await this.createTestLanguage({
                testId: this.routetestId,
                languageId: parseInt(this.newLanguage)
            })

            await this.GetTestLanguages();
            this.closeLanguagePopup();
        },
        openAddLanguagePopup() {

            this.fetchAvailableLanguages();
            this.languagePopupText = 'Add language to test'
            this.showAddLanguagePopup = true;
        },
        openDeleteLanguagePopup() {
            this.languagePopupText = 'Delete language from test'
            this.showDeleteLanguagePopup = true;
        },
        closeLanguagePopup() {
            this.languagePopupText = ''
            if (this.showAddLanguagePopup)
                this.showAddLanguagePopup = false;
            if (this.showDeleteLanguagePopup)
                this.showDeleteLanguagePopup = false;
            this.newLanguage = null;
        },

        toggleTestView() {
            this.testExpanded = !this.testExpanded;
        },

        startTestEdit() {
            this.testEditing = true;
            this.sectionEditing = false;
            if(this.instruction)
                this.testEditContent = this.sanitizeHtmlForQuill(this.instruction);
            else
                this.testEditContent = '';
        },

        cancelTestEdit() {
            this.testEditing = false;
            this.testEditContent = '';
        },

        async saveTestInstructions() {
            if (this.testEditContent.trim().length < 15) {
                toast.error('Please enter test instructions.');
                return;
            }
            try {
                await this.putTestInstruction({
                    testId: this.routetestId,
                    languageId: this.selectedLanguage,
                    instruction: this.testEditContent
                });

                this.instruction = this.testEditContent;
                this.testEditing = false;
                this.testEditContent = '';
                await this.loadInstructions();
            } catch (error) {
                toast.error('Error saving test instructions. Please try again.');
            }
        },

        toggleSectionView() {
            this.sectionExpanded = !this.sectionExpanded;
        },

        startSectionEdit() {
            this.sectionEditing = true;
            this.testEditing = false;
            this.sectionEditContent = this.sanitizeHtmlForQuill(this.sectionInstruction || '');
        },

        cancelSectionEdit() {
            this.sectionEditing = false;
            this.sectionEditContent = '';
        },

        async saveSectionInstructions() {
            if (this.sectionEditContent.trim().length < 5) {
                toast.error('Please enter section instructions.');
                return;
            }
            try {
                await this.putTestSectionInstructions({
                    testId: this.routetestId,
                    languageId: parseInt(this.selectedLanguage),
                    sectionId: parseInt(this.selectedSection),
                    instruction: this.sectionEditContent
                });
                this.sectionInstruction = this.sectionEditContent;
                this.sectionEditing = false;
                this.sectionEditContent = '';
                await this.loadInstructions();
            } catch (error) {
                console.error('Error saving section instructions:', error);
            }
        },

        selectSection(section) {
            this.selectedSection = parseInt(section);
            this.isOpen = false;
            this.filterSectionInstruction();
        },

        handleBlur() {
            setTimeout(() => {
                this.isOpen = false;
            }, 0);
        },

        async selectLanguage(langId) {
            this.selectedLanguage = parseInt(langId);
            this.cancelTestEdit();
            this.cancelSectionEdit();
            await this.loadInstructions();
            return
        },
        prepareDelete(lang) {
            this.langToDelete = lang;
            this.deleteText = `Are you sure you want to delete "${lang.languageName}"?`;
            this.showDeletePopup = true;
        },
        cancelDelete() {
            this.showDeletePopup = false;
            this.langToDelete = null;
        },
        async deleteLanguage() {
            const deletedId = this.newLanguage;

            const index = this.languages.findIndex(l => l.languageId === deletedId);

            await this.deleteTestLanguage({
                testId: this.routetestId,
                languageId: parseInt(deletedId),
            });
            await this.GetTestLanguages();

            if (this.languages.length > 0) {
                const prevIndex = index > 0 ? index - 1 : 0;
                this.selectedLanguage = this.languages[prevIndex]
                    ? this.languages[prevIndex].languageId
                    : null;
            } else {
                this.selectedLanguage = null;
            }
            this.closeLanguagePopup();
        }

    }
}
</script>

<style>
.containerI {
    width: 100%;
    margin: 0 auto;
    background: white;
    border-radius: 5px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    /* overflow: visible; */
}

.language-selector {
    display: flex;
    align-items: center;
    margin-bottom: 0px;
    width: 100%;
}

.languageContainer{
    display: flex;
    align-items: center;
    gap: 13px;
    margin: 24px;
    margin-bottom: 0px;
    padding-bottom: 10px;
    overflow-x: auto;
}

.language-btn {
    padding: 4px 15px;
    color: #4B2EEE;
    background-color: transparent;
    font-size: 15px;
    font-weight: 500;
    border-radius: 8px;
    border: 2px solid #4B2EEE;
    transition: all 0.2s;
}

.language-btn:hover {
    color: #f8fafc;
    background: #4B2EEE;
    border-color: #4B2EEE;
}

.language-btn.active {
    color: #f8fafc;
    background: #4B2EEE;
}

.language-wrapper {
    position: relative;
    overflow: visible;
}

.language-btn.delete-mode {
    background: #dc3545;
    border-color: #dc3545;
    color: white;
}

.addDeleteBtn {
    display: flex;
    gap: 13px;
    margin: 26px 0 22px 22px;
}

.section {
    padding: 12px;
    margin: 24px;
    background: rgba(51, 51, 51, 0.03);
    border-radius: 6px;
    min-height: 220px;
    height: auto;
    overflow: hidden;
    z-index: 0;
}

.section.expanded {
    min-height: 330px;
}

.section-header {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
    height: 30px;
    gap: 8px;
}

.section-title {
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
}

.section-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    z-index: 1;
}

.action-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    border: none;
    background: rgb(238, 236, 236);
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 800;
    color: #4B2EEE;
    transition: all 0.2s;
}

.action-btn:hover {
    background-color: #f9fafb;
    border-color: #9ca3af;
}

.action-btn.primary {
    background-color: #6366f1;
    padding: 8px 16px;
    font-size: 14px;
    color: white;
    border-color: #6366f1;
}

.action-btn.primary:hover {
    background-color: #5b5fef;
}

.action-btn.cancel {
    border-radius: 8px;
    padding: 8px 16px;
    font-size: 14px;
    color: black;
    background: #E2E2E2;
}

.action-btn.cancel:hover {
    background-color: #989595;
}

.instruction-content {
    background-color: #ffffff;
    border: 2px solid #bfc4ca;
    border-radius: 8px;
    padding: 16px;
    min-height: 220px;
    font-size: 14px;
    line-height: 1.6;
    color: inherit;
    white-space: pre-wrap;
    overflow-y: auto !important;
    position: relative;
}

.instruction-content .ql-editor {
    min-height: 220px;
    padding: 16px;
    background-color: #ffffff;
    color: inherit;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.6;
}

.instruction-content .ql-editor p,
.instruction-content .ql-editor span,
.instruction-content .ql-editor h1,
.instruction-content .ql-editor h2,
.instruction-content .ql-editor h3,
.instruction-content .ql-editor h4,
.instruction-content .ql-editor h5,
.instruction-content .ql-editor h6 {
    margin: 0 0 0.5em 0;
    font-size: inherit;
    line-height: inherit;
}

.custom-dropdown {
    position: relative;
    min-width: auto;
    display: inline-block;
}

.custom-dropdown select {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-color: #f8f9fa;
    border: 2px solid #54728c;
    border-radius: 6px;
    padding: 6px 40px 6px 14px;
    font-size: 16px;
    font-weight: 600;
    color: #2c3e50;
    cursor: pointer;
}

.custom-dropdown .dropdown-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #54728c;
}

.collapsed-text {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: auto;
    max-height: 4.8em;
}

.popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.popup-content {
    background: white;
    padding: 15px;
    border-radius: 8px;
    width: 400px;
    max-width: 90%;
}

.language-select {
    width: 100%;
    padding: 8px;
    border: 1px solid #c7cacd;
    border-radius: 6px;
    font-size: 14px;
}

.popup-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
}

.is-disabled {
    opacity: 0.6;
    cursor: not-allowed !important;
    /* pointer-events: none;  */
}

@media (min-width: 1281px){
    .languageContainer{
    width: 85vw;
}
 .addDeleteBtn{
    width:17vw;
    margin-left: 0px;
 }
}
@media ((min-width: 990px) and (max-width:1281px)){
    .languageContainer{
    width: 71vw !important;
}
.addDeleteBtn{
    width: 24vw;
    margin-left: 0px;
 }
}
@media (max-width: 990px) {
    .language-selector{
        flex-wrap: wrap;
    }
    .addDeleteBtn{
    margin-left: 24px;
    margin-bottom: 0;
 }
    .section-header {
        height: auto;
    }

    .section-actions {
        justify-content: flex-start;
    }
}
</style>